import React, { useCallback } from 'react'
import { addToCheckoutCart } from 'actions/CheckoutActions'
import { generateBNBLCheckoutItem } from 'lib/checkout/accommodation/cart'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { useAppDispatch } from 'hooks/reduxHooks'
import TextButton from 'components/Luxkit/Button/TextButton'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { getBnblPackageViewsTotalPrice, getFromDuration } from 'selectors/offerPage/offerPageSelectors'
import { BnblTotalPackagesPrices } from 'components/OfferPage/BnblModal/utils'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowTaxesAndFees from 'components/Luxkit/PricePoints/PriceRowTaxesAndFees'
import Group from 'components/utils/Group'
import { addToCart } from 'analytics/snowplow/events'
import * as Analytics from 'analytics/analytics'
import { OptimizelyServerSideExperiments, getOptimizelyExperimentVariation } from 'lib/optimizely/optimizelyUtils'
import { safeDivideAndCeil } from 'lib/maths/mathUtils'
import LuxuryPlusPriceStack from 'loyalty/components/LuxuryPlusPriceStack'

interface MappedProps {
  bnblModalPackages: App.BnblModal[];
  totalRoomsPrice: BnblTotalPackagesPrices;
  isPerNightPricingTestEnabled: boolean;
  duration: number;
}

interface Props {
  offer: App.Offer;
  isGift: boolean;
}

const getPriceByNight = (totalRoomPrices: BnblTotalPackagesPrices, nights: number):BnblTotalPackagesPrices => {
  return {
    ...totalRoomPrices,
    hotelValue: safeDivideAndCeil(totalRoomPrices.hotelValue, nights),
    hotelPrice: safeDivideAndCeil(totalRoomPrices.hotelPrice, nights),
    priceFrom: safeDivideAndCeil(totalRoomPrices.priceFrom, nights),
    hotelMemberPrice: safeDivideAndCeil(totalRoomPrices.hotelMemberPrice, nights),
    memberPriceFrom: safeDivideAndCeil(totalRoomPrices.memberPriceFrom, nights),
  }
}

function BnblPackagePricing(props: Props & MappedProps) {
  const {
    bnblModalPackages,
    totalRoomsPrice,
    offer,
    isGift,
    isPerNightPricingTestEnabled,
    duration,
  } = props

  const dispatch = useAppDispatch()

  const { hotelValue, hotelPrice, priceFrom, shouldDisplayValue, hotelMemberPrice, memberPriceFrom } = isPerNightPricingTestEnabled ? getPriceByNight(totalRoomsPrice, duration) : totalRoomsPrice
  const saleUnit = isPerNightPricingTestEnabled ? 'night' : offer.saleUnit

  const shouldShowValue = hotelValue > 0 && hotelValue > (memberPriceFrom || priceFrom) && shouldDisplayValue
  const valueOffPercent = getValueOffPercent(hotelValue, (hotelMemberPrice || hotelPrice))
  const shouldShowRate = valueOffPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const onBuyNow = useCallback(() => {
    const checkoutItems = bnblModalPackages.reduce<Array<App.Checkout.BNBLLEHotelItem>>((array, modalPackage) => {
      const items = generateBNBLCheckoutItem(offer, modalPackage.packageView.package, modalPackage.numberOfRooms)
      return [...array, ...items]
    }, [])
    // TODO: isGift cannot work with multiple cart items
    dispatch(addToCheckoutCart(checkoutItems, isGift ? { isGift } : undefined))
    Analytics.track('add-to-cart', addToCart())
    dispatch(navigateToCheckout())
  }, [isGift, offer, bnblModalPackages, dispatch])

  return (
    <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
      <div>
        <PriceRowPriceCaption>From</PriceRowPriceCaption>
        <LuxuryPlusPriceStack
          price={priceFrom}
          memberPrice={!isGift ? memberPriceFrom : 0}
          size="S"
          saleUnit={saleUnit}
          taxesAndFees="inclusive"
          loyaltyConfig={offer.loyalty}
        />
        {shouldShowValue && <PriceRowValueDiscountWithCaption
          size="M"
          value={hotelValue}
          discountPercentage={shouldShowRate ? valueOffPercent : undefined}
        />}
        <PriceRowTaxesAndFees taxesAndFees="inclusive" />
      </div>
      <TextButton data-testid="bnbl-buy-btn" kind="primary" onClick={onBuyNow} size="large">
        Buy now
      </TextButton>
    </Group>
  )
}

export default withOfferPageAndReduxState((offerPageState, appState: App.State, ownProps: Props): MappedProps => {
  return {
    totalRoomsPrice: getBnblPackageViewsTotalPrice(offerPageState, appState),
    bnblModalPackages: appState.offer.bnblModal[ownProps.offer.id],
    isPerNightPricingTestEnabled: !!getOptimizelyExperimentVariation(appState, OptimizelyServerSideExperiments.pricePerNight),
    duration: getFromDuration(offerPageState, appState),
  }
})(BnblPackagePricing)
